import { useHeaderStateProvider } from '@/context/HeaderStateContext';
import {
  Button,
  Checkbox,
  Divider,
  Layout,
  Space,
  Tooltip,
  TreeSelect,
} from 'antd';
import React, {
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
} from 'react';
import FullscreenButton from '../screenToggle';
import DateRangePicker from '../date-range-picker/DateRangePicker';
import { ProfileDropDown } from '../layout/ProfileDropDown';
import { useConversations } from '@/lib/api/hooks/useAi';
import { useLocation } from 'react-router-dom';
import CountrySwitcher from '../country/CountrySwitcher';
import GlobalSearch from '../GlobalSearch';
import NotificationBell from '../notifications/NotificationBell';

const { Header } = Layout;

interface TreeNode {
  title: string | React.ReactNode;
  value: string;
  selectable?: boolean;
  checkable?: boolean;
  children?: { title: React.ReactNode; value: string }[];
}

const EXCLUDED_ROUTES = [
  '/dashboard/profile',
  '/dashboard/profile/edit',
  '/dashboard/profile/password',
  '/dashboard/user-management',
  '/dashboard/asin-categorization',
  '/dashboard/profile/billing',
  '/dashboard/profile/invoices',
  '/dashboard/profile/refund-and-cancellation',
  '/dashboard/profile/terms-and-services',
  '/dashboard/profile/privacy-policy',
];

const EXCLUDED_ROUTES_FOR_COUNTRY_SWITCHER = [
  '/dashboard/profile',
  '/dashboard/profile/edit',
  '/dashboard/profile/password',
  '/dashboard/user-management',
  '/dashboard/asin-categorization',
];

const DashboardHeader1: React.FC = () => {
  const location = useLocation();
  const { adAccounts, setAdAccounts } = useHeaderStateProvider();
  const isSellcoAiRoute = location.pathname.includes('/dashboard/sellco-ai');
  const isAdsRoute = location.pathname.toLowerCase().includes('/ads');
  const NON_ADS_PROFILES_KEY = 'app_v2_nonads_profiles';
  const ADS_PROFILES_KEY = 'app_v2_ads_profiles';
  const prevIsAdsRouteRef = useRef(isAdsRoute);
  const nonAdsSelectionRef = useRef<string[] | null>(null);

  // Reset initialization when route type changes to prevent flickering
  useEffect(() => {
    if (prevIsAdsRouteRef.current !== isAdsRoute) {
      setIsInitializing(true);
      setHasUserInteracted(false);
      prevIsAdsRouteRef.current = isAdsRoute;
    }
  }, [isAdsRoute]);
  const { data: conversationsData } = useConversations({
    enabled: isSellcoAiRoute,
  });

  const shouldRenderControls = useMemo(
    () =>
      !EXCLUDED_ROUTES.some((route) => location.pathname === route) &&
      !isSellcoAiRoute,
    [location.pathname, isSellcoAiRoute]
  );

  const shouldRenderCountrySwiter = useMemo(
    () =>
      !EXCLUDED_ROUTES_FOR_COUNTRY_SWITCHER.some(
        (route) => location.pathname === route
      ),
    [location.pathname]
  );

  const [tempValue, setTempValue] = useState<string[]>([]);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Use local state for dropdown visibility to avoid context re-renders
  const [localDropdownVisible, setLocalDropdownVisible] = useState(false);

  // Utility to update account and profile selection
  const updateSelection = useCallback(
    (selectedProfileIds: string[]) => {
      const updatedAccounts = adAccounts.map((account) => {
        const hasSelectedProfiles = account.profiles.some((profile) =>
          selectedProfileIds.includes(profile.profileId)
        );
        return {
          ...account,
          selected: hasSelectedProfiles,
          profiles: account.profiles.map((profile) => ({
            ...profile,
            selected: selectedProfileIds.includes(profile.profileId),
          })),
        };
      });

      const currentProfileIds = adAccounts
        .flatMap((account) =>
          account.profiles.filter((p) => p.selected).map((p) => p.profileId)
        )
        .sort();
      const newProfileIds = selectedProfileIds.sort();
      if (JSON.stringify(currentProfileIds) !== JSON.stringify(newProfileIds)) {
        setAdAccounts(updatedAccounts);
        setTempValue(selectedProfileIds);
      }
    },
    [adAccounts, setAdAccounts]
  );

  // Initialize selection - improved to prevent flickering
  useEffect(() => {
    if (adAccounts.length > 0 && !hasUserInteracted && isInitializing) {
      const selectedProfileIds = adAccounts
        .flatMap((account) =>
          account.profiles.filter((profile) => profile.selected)
        )
        .map((profile) => profile.profileId);

      const allIds = adAccounts.flatMap((account) =>
        account.profiles.map((profile) => profile.profileId)
      );

      // Prefer persisted selection for non-ads pages when available
      if (!isAdsRoute) {
        try {
          const raw = localStorage.getItem(NON_ADS_PROFILES_KEY);
          if (raw) {
            const stored: unknown = JSON.parse(raw);
            if (Array.isArray(stored)) {
              const valid = stored.filter(
                (id): id is string =>
                  typeof id === 'string' && allIds.includes(id)
              );
              if (valid.length > 0) {
                if (
                  JSON.stringify((tempValue as string[]).slice().sort()) !==
                  JSON.stringify(valid.slice().sort())
                ) {
                  setTempValue(valid);
                }
                updateSelection(valid);
                return;
              }
            }
          }
        } catch {}
      }

      if (selectedProfileIds.length === 0) {
        // On Ads pages, default to a single profile locally; do not mutate global
        const initialIds = isAdsRoute ? (allIds[0] ? [allIds[0]] : []) : allIds;
        if (isAdsRoute) {
          if (
            JSON.stringify((tempValue as string[]).slice().sort()) !==
            JSON.stringify(initialIds.slice().sort())
          ) {
            setTempValue(initialIds);
          }
        } else {
          updateSelection(initialIds);
        }
      } else {
        // On Ads pages, coerce to a single selection locally (first one wins)
        const normalized = isAdsRoute
          ? selectedProfileIds.length > 0
            ? [selectedProfileIds[0]]
            : []
          : selectedProfileIds;
        if (
          JSON.stringify((tempValue as string[]).slice().sort()) !==
          JSON.stringify(normalized.slice().sort())
        ) {
          setTempValue(normalized);
        }
      }

      // Mark initialization as complete to prevent re-runs
      setIsInitializing(false);
    }
  }, [adAccounts, hasUserInteracted, isAdsRoute, isInitializing]); // Removed tempValue and updateSelection to prevent flickering

  const getPageTitle = useCallback(() => {
    const path = location.pathname.split('/').pop() || '';
    if (isSellcoAiRoute && Array.isArray(conversationsData)) {
      const conversation = conversationsData.find((conv) => conv.id === path);
      if (conversation?.title) return conversation.title;
    }
    return path
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
  }, [location.pathname, isSellcoAiRoute, conversationsData]);

  const treeData: TreeNode[] = useMemo(
    () =>
      adAccounts.map((account) => ({
        title: `${account.name} (${account.profiles.length})`,
        value: `account_${account.id}`,
        selectable: true,
        checkable: true,
        children: account.profiles.map((profile) => ({
          title: (
            <span>
              {profile.accountName} <br />
              <strong>{profile.profileId}</strong>
            </span>
          ),
          value: profile.profileId,
        })),
      })),
    [adAccounts]
  );

  const allProfileIds = useMemo(
    () =>
      adAccounts.flatMap((account) =>
        account.profiles.map((profile) => profile.profileId)
      ),
    [adAccounts]
  );

  // Memoize profile lookup map for better performance
  const profileLookupMap = useMemo(() => {
    const map = new Map();
    adAccounts.forEach((account) => {
      account.profiles.forEach((profile) => {
        map.set(profile.profileId, profile);
      });
    });
    return map;
  }, [adAccounts]);

  // Memoize account lookup map for better performance
  const accountLookupMap = useMemo(() => {
    const map = new Map();
    adAccounts.forEach((account) => {
      map.set(account.id, account);
    });
    return map;
  }, [adAccounts]);

  const onApply = useCallback(() => {
    if (!isAdsRoute) {
      updateSelection(tempValue);
    } else {
      // For ads routes, dispatch a custom event to notify data fetching hooks
      window.dispatchEvent(
        new CustomEvent('adsProfileChanged', {
          detail: { profileIds: tempValue },
        })
      );
    }
    setLocalDropdownVisible(false);
  }, [tempValue, updateSelection, isAdsRoute]);

  const handleTreeSelectChange = useCallback(
    (values: string[]) => {
      const newSelectedProfiles: string[] = [];

      values.forEach((value) => {
        if (value.startsWith('account_')) {
          if (isAdsRoute) {
            // Ignore account-level bulk selection on Ads
            return;
          }
          const accountId = parseInt(value.replace('account_', ''));
          const account = accountLookupMap.get(accountId);
          if (account) {
            account.profiles.forEach((profile) => {
              newSelectedProfiles.push(profile.profileId);
            });
          }
        } else {
          newSelectedProfiles.push(value);
        }
      });

      // Remove duplicates and enforce single selection on Ads
      let uniqueProfiles = [...new Set(newSelectedProfiles)];
      if (isAdsRoute && uniqueProfiles.length > 0) {
        uniqueProfiles = [uniqueProfiles[uniqueProfiles.length - 1]];
      }
      setTempValue(uniqueProfiles);
      setHasUserInteracted(true);
      try {
        localStorage.setItem(
          isAdsRoute ? ADS_PROFILES_KEY : NON_ADS_PROFILES_KEY,
          JSON.stringify(uniqueProfiles)
        );
      } catch {}
    },
    [accountLookupMap, isAdsRoute]
  );

  const collapseTriggerStyle: React.CSSProperties = {
    backgroundColor: '#232f3e',
    color: 'rgba(255, 255, 255, 0.65)',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'color 0.3s',
  };

  const placeholderText = useMemo(() => {
    return tempValue.length === 0
      ? 'Select Accounts'
      : `Accounts (${tempValue.length} selected)`;
  }, [tempValue.length]);

  const maxTagPlaceholder = useCallback(
    (omittedValues: any[]) => (
      <Tooltip
        title={omittedValues
          .map((val) => {
            const profile = profileLookupMap.get(val);
            return profile?.accountName || val;
          })
          .join(', ')}
      >
        <span>+{omittedValues.length} more</span>
      </Tooltip>
    ),
    [profileLookupMap]
  );

  // On transition between routes, capture non-ads selection on entering Ads; restore when leaving Ads
  useEffect(() => {
    const wasAds = prevIsAdsRouteRef.current;
    // Entering Ads from Non-Ads: snapshot current non-ads selection from global state
    if (!wasAds && isAdsRoute) {
      const currentSelected = adAccounts
        .flatMap((account) => account.profiles)
        .filter((p) => p.selected)
        .map((p) => p.profileId);
      // If nothing selected, snapshot all
      const allIds = adAccounts.flatMap((account) =>
        account.profiles.map((profile) => profile.profileId)
      );
      nonAdsSelectionRef.current =
        currentSelected.length > 0 ? currentSelected : allIds;
    }
    // Leaving Ads to Non-Ads: restore snapshot
    if (wasAds && !isAdsRoute) {
      const allIds = adAccounts.flatMap((account) =>
        account.profiles.map((profile) => profile.profileId)
      );
      const desired =
        nonAdsSelectionRef.current && nonAdsSelectionRef.current.length > 0
          ? nonAdsSelectionRef.current
          : allIds;
      setTempValue(desired);
      updateSelection(desired);
    }
    prevIsAdsRouteRef.current = isAdsRoute;
  }, [isAdsRoute, adAccounts, updateSelection]);

  // Enforce single selection whenever on Ads pages, regardless of prior pages
  useEffect(() => {
    if (!isAdsRoute) return;
    const allIds = adAccounts.flatMap((account) =>
      account.profiles.map((profile) => profile.profileId)
    );
    let next = tempValue;
    if (next.length === 0 && allIds[0]) {
      next = [allIds[0]];
    } else if (next.length > 1) {
      next = [next[0]];
    }
    if (JSON.stringify(next) !== JSON.stringify(tempValue)) {
      setTempValue(next);
      // Do not mutate global selection while on Ads
    }
  }, [isAdsRoute, adAccounts, tempValue]);

  // Header1 now operates independently - no cross-context synchronization

  // Persist selection on any change (covers Select All, Apply, programmatic updates)
  useEffect(() => {
    try {
      const key = isAdsRoute ? ADS_PROFILES_KEY : NON_ADS_PROFILES_KEY;
      const serialized = JSON.stringify(tempValue);
      // Avoid unnecessary writes
      const existing = localStorage.getItem(key);
      if (existing !== serialized) {
        localStorage.setItem(key, serialized);
      }
    } catch {}
  }, [tempValue, isAdsRoute]);

  const popupRender = useCallback(
    (menu: React.ReactNode) => {
      const isAllSelected = tempValue.length === allProfileIds.length;
      const toggleSelectAll = () => {
        setHasUserInteracted(true);
        setTempValue(isAllSelected ? [] : allProfileIds);
      };

      return (
        <>
          {!isAdsRoute && (
            <>
              <div
                style={{
                  padding: '8px 12px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <Checkbox checked={isAllSelected} onChange={toggleSelectAll}>
                  Select All
                </Checkbox>
              </div>
              <Divider style={{ margin: '4px 0' }} />
            </>
          )}
          {menu}
          <Divider style={{ margin: '4px 0' }} />
          <div style={{ padding: 8 }}>
            <Button type="primary" block onClick={onApply}>
              Apply
            </Button>
          </div>
        </>
      );
    },
    [
      tempValue.length,
      allProfileIds,
      onApply,
      setHasUserInteracted,
      location.pathname,
    ]
  );

  return (
    <Header
      style={{
        ...collapseTriggerStyle,
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: '1.2rem',
          fontWeight: 500,
          lineHeight: '1.5715',
          textTransform: 'uppercase',
          color: '#fff',
          letterSpacing: '1.5px',
        }}
      >
        {isSellcoAiRoute ? <p className="!mt-5"> Sellco AI</p> : getPageTitle()}
      </h2>

      <div className="flex items-center gap-4">
        <FullscreenButton />

        {/* Global Search - Always visible */}
        <GlobalSearch placeholder="Search pages and features..." />

        {shouldRenderControls && (
          <Space size="large">
            {/* <FullscreenButton /> */}
            <TreeSelect
              className="custom-treeselect"
              style={{ width: 250 }}
              value={tempValue}
              multiple
              styles={{ popup: { root: { maxHeight: 400, overflow: 'auto' } } }}
              treeData={treeData}
              placeholder={placeholderText}
              treeDefaultExpandAll={false}
              onChange={handleTreeSelectChange}
              open={localDropdownVisible}
              onOpenChange={setLocalDropdownVisible}
              treeCheckable
              showCheckedStrategy={TreeSelect.SHOW_PARENT}
              maxTagCount={1}
              maxTagPlaceholder={maxTagPlaceholder}
              popupRender={popupRender}
            />
            <div className="flex gap-2 justify-between items-center">
              <DateRangePicker />
            </div>
          </Space>
        )}
        {shouldRenderCountrySwiter && <CountrySwitcher />}
        <NotificationBell />
        <ProfileDropDown />
      </div>
    </Header>
  );
};

export default DashboardHeader1;

// import { useHeaderStateProvider } from '@/context/HeaderStateContext';
// import {
//   Button,
//   Checkbox,
//   Divider,
//   Layout,
//   Space,
//   Tooltip,
//   TreeSelect,
// } from 'antd';
// import React, { useEffect, useMemo, useCallback, useState } from 'react';
// import FullscreenButton from '../screenToggle';
// import DateRangePicker from '../date-range-picker/DateRangePicker';
// import { ProfileDropDown } from '../layout/ProfileDropDown';
// import { useConversations } from '@/lib/api/hooks/useAi';
// import { useLocation } from 'react-router-dom';
// import CountrySwitcher from '../country/CountrySwitcher';

// const { Header } = Layout;

// interface TreeNode {
//   title: string | React.ReactNode;
//   value: string;
//   selectable?: boolean;
//   checkable?: boolean;
//   children?: { title: React.ReactNode; value: string }[];
// }

// const EXCLUDED_ROUTES = [
//   '/dashboard/profile',
//   '/dashboard/profile/edit',
//   '/dashboard/profile/password',
//   '/dashboard/user-management',
//   '/dashboard/accounts',
//   '/dashboard/asin-categorization',
//   '/dashboard/profile/billing',
//   '/dashboard/profile/invoices',
// ];

// const DashboardHeader1: React.FC = () => {
//   const location = useLocation();
//   const { adAccounts, setAdAccounts } = useHeaderStateProvider();
//   const isSellcoAiRoute = location.pathname.includes('/dashboard/sellco-ai');
//   const { data: conversationsData } = useConversations({
//     enabled: isSellcoAiRoute,
//   });

//   const shouldRenderControls = useMemo(
//     () =>
//       !EXCLUDED_ROUTES.some((route) => location.pathname === route) &&
//       !isSellcoAiRoute,
//     [location.pathname, isSellcoAiRoute]
//   );

//   const [tempValue, setTempValue] = useState<string[]>([]);
//   const [hasUserInteracted, setHasUserInteracted] = useState(false);
//   const [localDropdownVisible, setLocalDropdownVisible] = useState(false);

//   // Get all valid profile IDs from adAccounts
//   const allProfileIds = useMemo(
//     () =>
//       adAccounts.flatMap((account) =>
//         account.profiles.map((profile) => profile.profileId)
//       ),
//     [adAccounts]
//   );

//   // Update selection
//   const updateSelection = useCallback(
//     (selectedProfileIds: string[]) => {
//       // Filter out invalid profile IDs
//       const validProfileIds = selectedProfileIds.filter((id) =>
//         allProfileIds.includes(id)
//       );

//       const updatedAccounts = adAccounts.map((account) => {
//         const hasSelectedProfiles = account.profiles.some((profile) =>
//           validProfileIds.includes(profile.profileId)
//         );
//         return {
//           ...account,
//           selected: hasSelectedProfiles,
//           profiles: account.profiles.map((profile) => ({
//             ...profile,
//             selected: validProfileIds.includes(profile.profileId),
//           })),
//         };
//       });

//       const currentProfileIds = adAccounts
//         .flatMap((account) =>
//           account.profiles.filter((p) => p.selected).map((p) => p.profileId)
//         )
//         .sort();
//       const newProfileIds = validProfileIds.sort();
//       if (JSON.stringify(currentProfileIds) !== JSON.stringify(newProfileIds)) {
//         setAdAccounts(updatedAccounts);
//         setTempValue(newProfileIds);
//       }
//     },
//     [adAccounts, setAdAccounts, allProfileIds]
//   );

//   // Initialize selection and sanitize tempValue
//   useEffect(() => {
//     if (adAccounts.length > 0 && !hasUserInteracted) {
//       const selectedProfileIds = adAccounts
//         .flatMap((account) =>
//           account.profiles.filter((profile) => profile.selected)
//         )
//         .map((profile) => profile.profileId)
//         .filter((id) => allProfileIds.includes(id)); // Ensure only valid IDs

//       if (selectedProfileIds.length === 0) {
//         updateSelection(allProfileIds);
//       } else if (
//         JSON.stringify(tempValue.sort()) !==
//         JSON.stringify(selectedProfileIds.sort())
//       ) {
//         setTempValue(selectedProfileIds);
//       }
//     }
//   }, [adAccounts, hasUserInteracted, tempValue, updateSelection, allProfileIds]);

//   const getPageTitle = useCallback(() => {
//     const path = location.pathname.split('/').pop() || '';
//     if (isSellcoAiRoute && Array.isArray(conversationsData)) {
//       const conversation = conversationsData.find((conv) => conv.id === path);
//       if (conversation?.title) return conversation.title;
//     }
//     return path
//       .split('-')
//       .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
//       .join(' ');
//   }, [location.pathname, isSellcoAiRoute, conversationsData]);

//   const treeData: TreeNode[] = useMemo(
//     () =>
//       adAccounts.map((account) => ({
//         title: `${account.name} (${account.profiles.length})`,
//         value: `account_${account.id}`,
//         selectable: true,
//         checkable: true,
//         children: account.profiles.map((profile) => ({
//           title: (
//             <span>
//               {profile.accountName} <br />
//               <strong>{profile.profileId}</strong>
//             </span>
//           ),
//           value: profile.profileId,
//         })),
//       })),
//     [adAccounts]
//   );

//   // Memoize profile lookup map
//   const profileLookupMap = useMemo(() => {
//     const map = new Map();
//     adAccounts.forEach((account) => {
//       account.profiles.forEach((profile) => {
//         map.set(profile.profileId, profile);
//       });
//     });
//     return map;
//   }, [adAccounts]);

//   // Memoize account lookup map
//   const accountLookupMap = useMemo(() => {
//     const map = new Map();
//     adAccounts.forEach((account) => {
//       map.set(account.id, account);
//     });
//     return map;
//   }, [adAccounts]);

//   const onApply = useCallback(() => {
//     updateSelection(tempValue);
//     setLocalDropdownVisible(false);
//   }, [tempValue, updateSelection]);

//   const handleTreeSelectChange = useCallback(
//     (values: string[]) => {
//       const newSelectedProfiles: string[] = [];

//       values.forEach((value) => {
//         if (value.startsWith('account_')) {
//           const accountId = parseInt(value.replace('account_', ''));
//           const account = accountLookupMap.get(accountId);
//           if (account) {
//             account.profiles.forEach((profile) => {
//               newSelectedProfiles.push(profile.profileId);
//             });
//           }
//         } else if (allProfileIds.includes(value)) { // Only include valid profile IDs
//           newSelectedProfiles.push(value);
//         }
//       });

//       // Remove duplicates
//       const uniqueProfiles = [...new Set(newSelectedProfiles)];
//       setTempValue(uniqueProfiles);
//       setHasUserInteracted(true);
//     },
//     [accountLookupMap, allProfileIds]
//   );

//   const collapseTriggerStyle: React.CSSProperties = {
//     backgroundColor: '#232f3e',
//     color: 'rgba(255, 255, 255, 0.65)',
//     borderTop: '1px solid rgba(255, 255, 255, 0.1)',
//     height: '48px',
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     cursor: 'pointer',
//     transition: 'color 0.3s',
//   };

//   const placeholderText = useMemo(() => {
//     return tempValue.length === 0
//       ? 'Select Accounts'
//       : `Accounts (${tempValue.length} selected)`;
//   }, [tempValue.length]);

//   const maxTagPlaceholder = useCallback(
//     (omittedValues: any[]) => (
//       <Tooltip
//         title={omittedValues
//           .map((val) => {
//             const profile = profileLookupMap.get(val);
//             return profile?.accountName || val;
//           })
//           .join(', ')}
//       >
//         <span>+{omittedValues.length} more</span>
//       </Tooltip>
//     ),
//     [profileLookupMap]
//   );

//   const popupRender = useCallback(
//     (menu: React.ReactNode) => {
//       const isAllSelected = tempValue.length === allProfileIds.length;
//       const toggleSelectAll = () => {
//         setHasUserInteracted(true);
//         setTempValue(isAllSelected ? [] : allProfileIds);
//       };

//       return (
//         <>
//           <div
//             style={{
//               padding: '8px 12px',
//               fontWeight: 500,
//               display: 'flex',
//               alignItems: 'center',
//               gap: 8,
//             }}
//           >
//             <Checkbox checked={isAllSelected} onChange={toggleSelectAll}>
//               Select All
//             </Checkbox>
//           </div>
//           <Divider style={{ margin: '4px 0' }} />
//           {menu}
//           <Divider style={{ margin: '4px 0' }} />
//           <div style={{ padding: 8 }}>
//             <Button type="primary" block onClick={onApply}>
//               Apply
//             </Button>
//           </div>
//         </>
//       );
//     },
//     [tempValue.length, allProfileIds, onApply]
//   );

//   return (
//     <Header
//       style={{
//         ...collapseTriggerStyle,
//         padding: '0 16px',
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'space-between',
//       }}
//     >
//       <h2
//         style={{
//           margin: 0,
//           fontSize: '1.2rem',
//           fontWeight: 500,
//           lineHeight: '1.5715',
//           textTransform: 'uppercase',
//           color: '#fff',
//           letterSpacing: '1.5px',
//         }}
//       >
//         {isSellcoAiRoute ? <p className="!mt-5"> Sellco AI</p> : getPageTitle()}
//       </h2>

//       <div className="flex items-center gap-4">
//         {shouldRenderControls && (
//           <Space size="large">
//             <FullscreenButton />
//             <TreeSelect
//               className="custom-treeselect"
//               style={{ width: 250 }}
//               value={tempValue}
//               multiple
//               styles={{ popup: { root: { maxHeight: 400, overflow: 'auto' } } }}
//               treeData={treeData}
//               placeholder={placeholderText}
//               treeDefaultExpandAll={false}
//               onChange={handleTreeSelectChange}
//               open={localDropdownVisible}
//               onOpenChange={setLocalDropdownVisible}
//               treeCheckable
//               showCheckedStrategy={TreeSelect.SHOW_PARENT}
//               maxTagCount={1}
//               maxTagPlaceholder={maxTagPlaceholder}
//               dropdownRender={popupRender}
//             />
//             <div className="flex gap-2 justify-between items-center">
//               <DateRangePicker />
//             </div>
//           </Space>
//         )}
//         {shouldRenderControls && <CountrySwitcher />}
//         <ProfileDropDown />
//       </div>
//     </Header>
//   );
// };

// export default DashboardHeader1;
